// Admin console application root. Built as its OWN bundle/container
// (deploy/charts/admin-web, host console.ewigbyte.company) so no admin code
// ships in the customer portal bundle. It talks to the same archive-service
// API — nginx proxies /v1 same-origin so the session cookie keeps working.
import { Navigate, Route, Routes } from 'react-router-dom';
import { api } from './api/client';
import { AuthContext } from './context/auth';
import { useAsync } from './lib/hooks';
import { portalUrl } from './lib/appUrls';
import { AppShell } from './components/AppShell';
import type { NavEntry } from './components/AppShell';
import { ToastProvider } from './components/Toast';
import { ErrorState, LoadingState } from './components/States';
import { Button } from './components/ui';
import {
  IconDashboard,
  IconUsers,
  IconArchive,
  IconLayers,
  IconRetention,
  IconAudit,
} from './components/Icons';
import { AdminOverview } from './pages/AdminOverview';
import { AdminCustomers } from './pages/AdminCustomers';
import { AdminBuckets } from './pages/AdminBuckets';
import { AdminBurns } from './pages/AdminBurns';
import { AdminBurnRequests } from './pages/AdminBurnRequests';
import { AdminUsers } from './pages/AdminUsers';
import { AdminCustomerDetail } from './pages/AdminCustomerDetail';
import { AdminSubscriptions } from './pages/AdminSubscriptions';
import { AdminPlans } from './pages/AdminPlans';
import { AdminFeatureFlags } from './pages/AdminFeatureFlags';
import { AdminVerification } from './pages/AdminVerification';

const ADMIN_NAV: NavEntry[] = [
  { to: '/', key: 'adminOverview', Icon: IconDashboard },
  { to: '/customers', key: 'adminCustomers', Icon: IconUsers },
  { to: '/buckets', key: 'adminBuckets', Icon: IconArchive },
  { to: '/burn-requests', key: 'adminBurnQueue', Icon: IconArchive },
  { to: '/burns', key: 'adminBurns', Icon: IconArchive },
  { to: '/users', key: 'adminUsers', Icon: IconUsers },
  { to: '/subscriptions', key: 'adminSubscriptions', Icon: IconLayers },
  { to: '/plans', key: 'adminPlans', Icon: IconLayers },
  { to: '/feature-flags', key: 'adminFlags', Icon: IconRetention },
  { to: '/verification', key: 'adminVerification', Icon: IconAudit },
];

export default function AdminApp() {
  const { status, data: me, error, reload } = useAsync(() => api.me(), []);

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <LoadingState />
      </div>
    );
  }

  if (status === 'error' || !me) {
    if (error?.isUnauthorized) {
      window.location.replace('/auth/login');
      return (
        <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
          <LoadingState />
        </div>
      );
    }
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
        <ErrorState error={error!} onRetry={reload} />
      </div>
    );
  }

  // Signed in but not a platform admin. The API already rejects every
  // /v1/admin/* call with 403, but rendering the console shell would be
  // misleading — send them to the customer portal instead.
  if (me.role !== 'admin') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
        <div className="state">
          <p className="state__title">Administrator access required</p>
          <p className="state__body">
            You are signed in as <strong>{me.username}</strong>, which is not a platform
            administrator account.
          </p>
          <a className="btn btn--primary" href={portalUrl('/')}>
            Go to the customer portal
          </a>
          <form method="POST" action="/logout" style={{ marginTop: 12 }}>
            <Button variant="ghost" type="submit">
              Log out
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ me }}>
      <ToastProvider>
        <Routes>
          <Route element={<AppShell nav={ADMIN_NAV} sectionKey="nav.sectionAdmin" brandTagKey="app.adminTagline" />}>
            <Route index element={<AdminOverview />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="customers/:id" element={<AdminCustomerDetail />} />
            <Route path="buckets" element={<AdminBuckets />} />
            <Route path="burn-requests" element={<AdminBurnRequests />} />
            <Route path="burns" element={<AdminBurns />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="feature-flags" element={<AdminFeatureFlags />} />
            <Route path="verification" element={<AdminVerification />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </ToastProvider>
    </AuthContext.Provider>
  );
}
